import { useState } from "react";
import useSWR from "swr";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
  Title,
} from "@mantine/core";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import { fetcher } from "../../../../services/fetcher";
import { explorePlansApi } from "../../services/explorePlansApi";
import {
  errorNotifications,
  successNotifications,
} from "../../../../utils/handleNotifications";

const emptyDay = (dayNumber) => ({
  day_number: dayNumber,
  location: "",
  altitude: "",
  risk_description: "",
  medical_facility: "",
  evacuation_note: "",
});

const ExplorePlanItineraryManage = () => {
  const { id: profileId } = useParams();
  const { data, isLoading, mutate } = useSWR(
    explorePlansApi.itineraryList(profileId),
    fetcher,
    { revalidateOnFocus: false }
  );

  const rows = Array.isArray(data) ? data : [];

  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(emptyDay(1));
  const [saving, setSaving] = useState(false);

  const openAdd = () => {
    const nextDay =
      rows.length > 0
        ? Math.max(...rows.map((r) => Number(r.day_number) || 0)) + 1
        : 1;
    setForm(emptyDay(nextDay));
    setAddOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      day_number: row.day_number,
      location: row.location || "",
      altitude: row.altitude || "",
      risk_description: row.risk_description || "",
      medical_facility: row.medical_facility || "",
      evacuation_note: row.evacuation_note || "",
    });
    setAddOpen(true);
  };

  const saveAddOrEdit = async () => {
    setSaving(true);
    try {
      if (editRow?.id) {
        await fetcher(explorePlansApi.itineraryDay(editRow.id), "PATCH", {
          day_number: Number(form.day_number),
          location: form.location,
          altitude: form.altitude,
          risk_description: form.risk_description,
          medical_facility: form.medical_facility,
          evacuation_note: form.evacuation_note,
        });
        successNotifications("Itinerary day updated");
      } else {
        await fetcher(explorePlansApi.itineraryList(profileId), "POST", {
          day_number: Number(form.day_number),
          location: form.location,
          altitude: form.altitude,
          risk_description: form.risk_description,
          medical_facility: form.medical_facility,
          evacuation_note: form.evacuation_note,
        });
        successNotifications("Itinerary day added");
      }
      setAddOpen(false);
      setEditRow(null);
      await mutate();
    } catch (e) {
      errorNotifications(e?.response?.data || "Save failed", e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm("Delete this itinerary day?")) return;
    try {
      await fetcher(explorePlansApi.itineraryDay(row.id), "DELETE");
      successNotifications("Itinerary day deleted");
      await mutate();
    } catch (e) {
      errorNotifications(e?.response?.data || "Delete failed", e);
    }
  };

  const normalizeBulk = (list) =>
    list.map((item, index) => ({
      day_number: Number(item.day_number || index + 1),
      location: item.location || "",
      altitude: item.altitude || "",
      risk_description: item.risk_description || "",
      medical_facility: item.medical_facility || "",
      evacuation_note: item.evacuation_note || "",
    }));

  const openBulk = () => {
    setBulkRows(
      rows.length
        ? rows.map((r) => ({
            day_number: r.day_number,
            location: r.location || "",
            altitude: r.altitude || "",
            risk_description: r.risk_description || "",
            medical_facility: r.medical_facility || "",
            evacuation_note: r.evacuation_note || "",
          }))
        : [emptyDay(1)]
    );
    setBulkOpen(true);
  };

  const saveBulk = async () => {
    setSaving(true);
    try {
      await fetcher(
        explorePlansApi.itineraryBulk(profileId),
        "PUT",
        normalizeBulk(bulkRows)
      );
      successNotifications("Itinerary saved");
      setBulkOpen(false);
      await mutate();
    } catch (e) {
      errorNotifications(e?.response?.data || "Bulk save failed", e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Box>
          <Title order={4} c="#092c4c" fw={700}>
            Itinerary days
          </Title>
          <Text size="sm" c="dimmed" mt={6}>
            Day-by-day route, altitude, risk, and medical notes for this
            profile.
          </Text>
        </Box>
        <Group gap="sm" wrap="wrap">
          <Button
            variant="outline"
            color="blue"
            size="sm"
            type="button"
            onClick={openBulk}
          >
            Bulk edit
          </Button>
          <Button
            variant="gradient"
            size="sm"
            type="button"
            gradient={{ from: "#0053A8", to: "#033F7C", deg: 90 }}
            onClick={openAdd}
          >
            + Add day
          </Button>
        </Group>
      </Group>

      <Paper radius="md" p={0} withBorder>
        <Table.ScrollContainer minWidth={1280}>
        <Table
          striped
          highlightOnHover
          styles={{
            th: {
              color: "#64748b",
              textTransform: "uppercase",
              fontSize: 11,
              letterSpacing: "0.05em",
              fontWeight: 600,
              background:
                "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
              whiteSpace: "nowrap",
            },
            td: {
              verticalAlign: "top",
              whiteSpace: "normal",
              wordBreak: "break-word",
            },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={60}>Day</Table.Th>
              <Table.Th w={220}>Location</Table.Th>
              <Table.Th w={100}>Altitude</Table.Th>
              <Table.Th miw={180}>Risk</Table.Th>
              <Table.Th miw={180}>Medical Facility</Table.Th>
              <Table.Th miw={180}>Evacuation Note</Table.Th>
              <Table.Th w={90}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">
                    Loading…
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="xl" fw={500}>
                    No itinerary days. Add some!
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.day_number}</Table.Td>
                  <Table.Td>{row.location || "—"}</Table.Td>
                  <Table.Td>{row.altitude || "—"}</Table.Td>
                  <Table.Td>{row.risk_description || "—"}</Table.Td>
                  <Table.Td>{row.medical_facility || "—"}</Table.Td>
                  <Table.Td>{row.evacuation_note || "—"}</Table.Td>
                  <Table.Td style={{ whiteSpace: "nowrap" }}>
                    <Group gap={6} wrap="nowrap" justify="center">
                      <Tooltip label="Edit" withArrow>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="sm"
                          radius="md"
                          type="button"
                          onClick={() => openEdit(row)}
                        >
                          <IconEdit size={15} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete" withArrow>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="sm"
                          radius="md"
                          type="button"
                          onClick={() => handleDelete(row)}
                        >
                          <IconTrash size={15} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
      </Paper>

      <Modal
        opened={addOpen}
        onClose={() => {
          setAddOpen(false);
          setEditRow(null);
        }}
        title={editRow ? "Edit itinerary day" : "Add itinerary day"}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Day number"
            type="number"
            value={form.day_number}
            onChange={(e) =>
              setForm((f) => ({ ...f, day_number: e.target.value }))
            }
          />
          <SimpleGrid cols={2}>
            <TextInput
              label="Location"
              value={form.location}
              onChange={(e) =>
                setForm((f) => ({ ...f, location: e.target.value }))
              }
            />
            <TextInput
              label="Altitude"
              value={form.altitude}
              onChange={(e) =>
                setForm((f) => ({ ...f, altitude: e.target.value }))
              }
            />
          </SimpleGrid>
          <TextInput
            label="Risk"
            value={form.risk_description}
            onChange={(e) =>
              setForm((f) => ({ ...f, risk_description: e.target.value }))
            }
          />
          <TextInput
            label="Medical facility"
            value={form.medical_facility}
            onChange={(e) =>
              setForm((f) => ({ ...f, medical_facility: e.target.value }))
            }
          />
          <TextInput
            label="Evacuation note"
            value={form.evacuation_note}
            onChange={(e) =>
              setForm((f) => ({ ...f, evacuation_note: e.target.value }))
            }
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button loading={saving} onClick={saveAddOrEdit}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk edit itinerary"
        size="xl"
        centered
      >
        <Text size="sm" c="dimmed" mb="sm">
          Replaces all days for this profile. Day numbers must be unique.
        </Text>
        <Stack gap="md" style={{ maxHeight: 420, overflow: "auto" }}>
          {bulkRows.map((row, idx) => (
            <Paper key={idx} p="sm" withBorder radius="md">
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="xs">
                <TextInput
                  label="Day"
                  type="number"
                  value={row.day_number}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], day_number: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  label="Location"
                  value={row.location}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], location: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  label="Altitude"
                  value={row.altitude}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], altitude: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  label="Risk"
                  value={row.risk_description}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], risk_description: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  label="Medical facility"
                  value={row.medical_facility}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], medical_facility: v };
                      return next;
                    });
                  }}
                />
                <TextInput
                  label="Evacuation"
                  value={row.evacuation_note}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBulkRows((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], evacuation_note: v };
                      return next;
                    });
                  }}
                />
              </SimpleGrid>
              <Group justify="flex-end" mt="xs">
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  onClick={() =>
                    setBulkRows((prev) => prev.filter((_, i) => i !== idx))
                  }
                >
                  Remove row
                </Button>
              </Group>
            </Paper>
          ))}
        </Stack>
        <Button
          size="xs"
          variant="light"
          mt="sm"
          onClick={() =>
            setBulkRows((prev) => [
              ...prev,
              emptyDay(
                prev.length
                  ? Math.max(
                      ...prev.map((p) => Number(p.day_number) || 0),
                      0
                    ) + 1
                  : 1
              ),
            ])
          }
        >
          + Add row
        </Button>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setBulkOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            gradient={{ from: "#0053A8", to: "#033F7C", deg: 90 }}
            loading={saving}
            onClick={saveBulk}
          >
            Save bulk
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
};

export default ExplorePlanItineraryManage;
